import { Autocomplete, AutocompleteItem, Avatar, Divider } from '@heroui/react'
import { countries } from '@/utils/countries'
import type { CountryType } from '@/utils/countries'
import type { Key } from 'react'

type Props = {
	label?: string
	name?: string
	onCountryChange?: (val: string) => void
	defaultValue?: string
	errorMessage?: string | undefined
	isInvalid?: boolean
	isRequired?: boolean
	className?: string
	isClearable?: boolean
}

// Create a modified countries list with the divider
const countriesWithDivider = [
	...countries.slice(0, 3),
	{ label: '', code: '', value: '' }, // Divider
	...countries.slice(3),
]

export default function CountrySelect({
	label = 'Country of Residence',
	onCountryChange,
	defaultValue = '',
	errorMessage,
	isInvalid,
	isRequired = false,
	className,
	isClearable = false,
}: Props) {
	return (
		<Autocomplete
			isRequired={isRequired}
			isClearable={isClearable}
			label={label}
			defaultInputValue={defaultValue}
			defaultSelectedKey={defaultValue || undefined}
			defaultItems={countriesWithDivider}
			errorMessage={errorMessage}
			isInvalid={isInvalid}
			onSelectionChange={(key: Key | null) => {
				// key is the AutocompleteItem's `key` prop (always the English label),
				// immune to Google Translate DOM text mutations.
				onCountryChange?.(key != null ? String(key) : '')
			}}
			className={className}
		>
			{(item: CountryType) => {
				// Special handling for the divider sentinel
				if (item.label === '') {
					return (
						<AutocompleteItem key="divider" className="pointer-events-none p-0">
							<Divider className="my-1" />
						</AutocompleteItem>
					)
				}

				return (
					<AutocompleteItem
						key={item.label}
						className="gap-2"
						startContent={
							<Avatar
								alt={item.label}
								className="!size-6 text-xs"
								src={`https://flagcdn.com/${item.code.toLowerCase()}.svg`}
							/>
						}
					>
						{item.label}
					</AutocompleteItem>
				)
			}}
		</Autocomplete>
	)
}
